import { useNavigate } from 'react-router-dom';
import { cn } from '@/shared/utils/cn';
import {
    notificationHref,
    notificationVisual,
    timeAgo,
    type Notification,
} from '@/modules/notifications/types';
import { useMarkReadMutation } from '@/modules/notifications/hooks/use-mark-read-mutation';

interface Props {
    notification: Notification;
    /** Called after a click navigates (e.g. to close the dropdown). */
    onNavigate?: () => void;
}

/** A single notification row — shared by the bell dropdown and the full page.
 *  Clicking marks it read and deep-links to the originating record (when one
 *  exists). Unread rows carry a left accent + dot. */
export function NotificationItem({ notification, onNavigate }: Props) {
    const navigate = useNavigate();
    const markRead = useMarkReadMutation();
    const { icon: Icon, tone } = notificationVisual(notification);
    const href = notificationHref(notification);
    const unread = !notification.read_at;

    const onClick = () => {
        if (unread) markRead.mutate(notification.id);
        if (href) {
            navigate(href);
            onNavigate?.();
        }
    };

    return (
        <button
            type="button"
            onClick={onClick}
            className={cn(
                'flex w-full items-start gap-3 px-4 py-3 text-left transition hover:bg-ink-50',
                unread && 'bg-brand-sky-50/40',
                !href && 'cursor-default',
            )}
        >
            <span className={cn('mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg', tone)}>
                <Icon className="h-4 w-4" />
            </span>
            <span className="min-w-0 flex-1">
                <span className="flex items-center justify-between gap-2">
                    <span className="truncate text-sm font-semibold text-brand-navy">{notification.title}</span>
                    <span className="shrink-0 font-ui text-[11px] text-ink-400">{timeAgo(notification.created_at)}</span>
                </span>
                {notification.body && (
                    <span className="mt-0.5 block truncate text-xs text-ink-500">{notification.body}</span>
                )}
            </span>
            {unread && <span className="mt-2 h-2 w-2 shrink-0 rounded-full bg-brand-sky" />}
        </button>
    );
}
